import { useDeploymentStore } from '@/store/deploymentStore';
import { CognitoService } from '@/auth/CognitoService';

/**
 * Reading what people sent to a published app — the waitlist entries, the RSVPs, the
 * contact messages.
 *
 * Visitors can only WRITE to these collections (the `webBookable` rule: anonymous create,
 * anonymous reads refused), so nobody can enumerate anyone else's entry. The owner reads
 * them with their own token, which is what this does.
 */
export interface SubmissionRow {
  id: string;
  createdAt?: string;
  [field: string]: unknown;
}

export type SubmissionsFailure = 'not-signed-in' | 'no-backend' | 'not-published' | 'request-failed';

export class SubmissionsError extends Error {
  readonly reason: SubmissionsFailure;
  constructor(reason: SubmissionsFailure) {
    super(MESSAGES[reason]);
    this.name = 'SubmissionsError';
    this.reason = reason;
  }
}

const MESSAGES: Record<SubmissionsFailure, string> = {
  'not-signed-in': 'Sign in to see who has replied.',
  'no-backend': 'No backend is configured, so there is nothing to read.',
  'not-published': 'Publish this app first — replies arrive once it is live.',
  'request-failed': "Couldn't load replies just now.",
};

/** How many replies one read returns. */
export const SUBMISSION_PAGE = 200;

export interface SubmissionPage {
  rows: SubmissionRow[];
  /** Pass back to read the next page; absent when there are no more. */
  cursor?: string;
}

/**
 * One page of replies, newest first — a reply you have not seen is the reason you opened
 * this.
 *
 * The sort is applied to whatever the caller has accumulated, not just this page: records
 * are keyed by a random id, so the server returns them in no useful order and sorting one
 * page at a time would interleave old replies among new ones on every "load more".
 */
export async function fetchSubmissions(
  hostedAppId: string | undefined,
  collection: string,
  cursor?: string,
  fetchImpl: typeof fetch = fetch,
): Promise<SubmissionPage> {
  const baseUrl = (useDeploymentStore.getState().config?.apiBaseUrl ?? '').replace(/\/+$/, '');
  if (!baseUrl) throw new SubmissionsError('no-backend');
  if (!hostedAppId) throw new SubmissionsError('not-published');
  const token = CognitoService.getAccessTokenSync();
  if (!token) throw new SubmissionsError('not-signed-in');

  const query = `?limit=${SUBMISSION_PAGE}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
  let res: Response;
  try {
    res = await fetchImpl(
      `${baseUrl}/apps/${encodeURIComponent(hostedAppId)}/${encodeURIComponent(collection)}${query}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch {
    throw new SubmissionsError('request-failed');
  }
  if (!res.ok) throw new SubmissionsError('request-failed');

  const body = (await res.json()) as { items?: SubmissionRow[]; cursor?: string };
  return { rows: sortNewestFirst(body.items ?? []), cursor: body.cursor };
}

export function sortNewestFirst(rows: SubmissionRow[]): SubmissionRow[] {
  return rows.slice().sort((a, b) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? '')));
}

/**
 * Replies as a CSV a spreadsheet will open.
 *
 * Columns are the union of every field across the rows, in first-seen order, so a reply
 * that answered an optional question does not shift everyone else's answers one column
 * left. `createdAt` leads, because the first thing anyone does with an export is sort by
 * when it arrived.
 *
 * Escaping follows RFC 4180: quotes double, and any value containing a comma, quote or
 * newline is quoted. A leading =, +, - or @ is prefixed with a quote — Excel and Sheets
 * treat those as formulas, so an unescaped submission is a spreadsheet that runs what a
 * stranger typed into a public form.
 */
export function submissionsToCsv(rows: SubmissionRow[]): string {
  const columns = ['createdAt'];
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (key !== 'updatedAt' && !columns.includes(key)) columns.push(key);
    }
  }

  const lines = [columns.map(cell).join(',')];
  for (const row of rows) lines.push(columns.map((c) => cell(row[c])).join(','));
  return lines.join('\r\n');
}

function cell(value: unknown): string {
  if (value === undefined || value === null) return '';
  let text = Array.isArray(value) || (typeof value === 'object') ? JSON.stringify(value) : String(value);
  if (/^[=+\-@]/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}
